// Hold-to-talk mic for Firefly Ask (PTT-3). Pointer down starts Deepgram;
// release auto-submits via the parent onRelease callback.

import { useCallback, useEffect, type PointerEvent } from "react";
import { useFireflyPttMic } from "./useFireflyPttMic";

type Props = {
  onAppend: (chunk: string) => void;
  onRelease: () => void;
  onListeningChange?: (listening: boolean) => void;
  disabled?: boolean;
};

export function FireflyAskMicButton({
  onAppend,
  onRelease,
  onListeningChange,
  disabled,
}: Props) {
  const { status, error, listening, start, stop } = useFireflyPttMic({
    onAppend,
    onStop: onRelease,
  });
  const busy = status === "starting" || status === "stopping";

  useEffect(() => {
    onListeningChange?.(listening);
  }, [listening, onListeningChange]);

  const beginHold = useCallback(
    (e: PointerEvent<HTMLButtonElement>) => {
      if (disabled || busy || e.button !== 0) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      void start();
    },
    [busy, disabled, start],
  );

  const endHold = useCallback(
    (e: PointerEvent<HTMLButtonElement>) => {
      if (disabled) return;
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      void stop();
    },
    [disabled, stop],
  );

  const label = listening ? "Listening…" : busy ? "…" : "Hold to ask";
  const ariaLabel = listening
    ? "Listening — release to ask"
    : busy
      ? "Mic warming up"
      : "Hold to ask with voice";

  return (
    <div className="flex flex-col items-start gap-1">
      <button
        type="button"
        disabled={Boolean(disabled) || busy}
        aria-pressed={listening}
        aria-label={ariaLabel}
        title={ariaLabel}
        className={
          "inline-flex touch-none select-none items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition disabled:cursor-not-allowed disabled:opacity-60 " +
          (listening
            ? "border-emerald-500 bg-emerald-100 text-emerald-900 dark:border-emerald-400 dark:bg-emerald-900/50 dark:text-emerald-100"
            : "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 dark:border-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-200 dark:hover:bg-emerald-900/50")
        }
        onPointerDown={beginHold}
        onPointerUp={endHold}
        onPointerCancel={endHold}
        onContextMenu={(e) => e.preventDefault()}
      >
        <span
          aria-hidden
          className={
            "inline-block h-1.5 w-1.5 rounded-full " +
            (listening ? "bg-emerald-600 animate-pulse" : "bg-emerald-500")
          }
        />
        <span>{label}</span>
      </button>
      {error ? (
        <span className="max-w-[12rem] text-[10px] text-red-700 dark:text-red-400">{error}</span>
      ) : null}
    </div>
  );
}
